import { motion } from "framer-motion";
import { useState } from "react";
import { api, SERVICES } from "../lib/api";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  address: "",
  service: SERVICES[0].label,
  date: "",
  time: "10:00",
  notes: "",
};

export default function Booking() {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.address || !form.date) {
      toast.error("Completa los campos obligatorios");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", form);
      setDone(true);
      setForm(emptyForm);
      toast.success("Solicitud enviada. Te responderemos por email pronto.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo enviar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="cita" data-testid="booking-section" className="py-24 md:py-40 bg-[color:var(--bg-secondary)]/70">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="col-span-12 md:col-span-5"
        >
          <div className="text-xs uppercase tracking-[0.28em] text-[color:var(--text-secondary)] mb-6">
            Reserva
          </div>
          <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.9] mb-8">
            Reserva tu <em className="italic text-[color:var(--accent)]">momento</em>.
          </h2>
          <p className="text-[color:var(--text-secondary)] max-w-md leading-relaxed mb-10">
            ¿Tienes alguna duda o quieres reservar una cita? Rellena el formulario y te ayudaremos por WhatsApp o correo en menos de 24 horas con toda la información que necesites.
          </p>
          <div className="space-y-4 font-mono text-xs uppercase tracking-widest text-[color:var(--text-secondary)]">
            <div className="flex justify-between border-b border-[color:var(--border-subtle)] pb-3"><span>Zona</span><span className="text-[color:var(--text-primary)] normal-case tracking-tight">Todas las áreas de Madrid</span></div>
            <div className="flex justify-between border-b border-[color:var(--border-subtle)] pb-3"><span>Respuesta</span><span className="text-[color:var(--text-primary)] normal-case tracking-tight">Menos de 24 h</span></div>
            <div className="flex justify-between border-b border-[color:var(--border-subtle)] pb-3"><span>Confirmación</span><span className="text-[color:var(--text-primary)] normal-case tracking-tight">Email + llamada</span></div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="col-span-12 md:col-span-7 bg-[color:var(--bg-primary)] border border-[color:var(--border-subtle)] rounded-2xl p-6 md:p-10"
          data-testid="booking-form"
        >
          {done ? (
            <div className="text-center py-16" data-testid="booking-success">
              <div className="font-heading text-4xl md:text-5xl italic mb-4">Gracias.</div>
              <p className="text-[color:var(--text-secondary)] mb-8">Hemos recibido tu solicitud. Te responderemos por email en menos de 24 horas para confirmar la disponibilidad y resolver cualquier duda.</p>
              <button type="button" onClick={() => setDone(false)} data-testid="booking-again" className="text-sm underline underline-offset-4">Nueva solicitud</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-5">
              <Field label="Nombre completo" required>
                <Input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="booking-name" className="input-cx" />
              </Field>
              <Field label="Teléfono" required>
                <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="booking-phone" className="input-cx" />
              </Field>
              <Field label="Email" required span={2}>
                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="booking-email" className="input-cx" />
              </Field>
              <Field label="Dirección" required span={2}>
                <Input value={form.address} onChange={(e) => update("address", e.target.value)} data-testid="booking-address" className="input-cx" placeholder="Calle, número, piso, ciudad" />
              </Field>
              <Field label="Servicio" span={2}>
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger data-testid="booking-service" className="input-cx h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.key} value={s.label}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Fecha" required>
                <Input type="date" value={form.date} onChange={(e) => update("date", e.target.value)} data-testid="booking-date" className="input-cx" />
              </Field>
              <Field label="Hora" required>
                <Input type="time" value={form.time} onChange={(e) => update("time", e.target.value)} data-testid="booking-time" className="input-cx" />
              </Field>
              <Field label="Notas" span={2}>
                <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} data-testid="booking-notes" className="input-cx min-h-[100px]" placeholder="Alergias, preferencias, acceso al edificio…" />
              </Field>
              <div className="col-span-2 flex items-center justify-between pt-2">
                <p className="text-xs text-[color:var(--text-secondary)]">Sin pago online. Confirmamos por email antes de la visita.</p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="booking-submit"
                  className="rounded-full bg-[color:var(--text-primary)] text-[color:var(--bg-primary)] px-8 py-3.5 text-sm hover:bg-[color:var(--signature-dark)] transition-colors disabled:opacity-60"
                >
                  {loading ? "Enviando…" : "Enviar solicitud →"}
                </button>
              </div>
            </div>
          )}
        </motion.form>
      </div>
      <style>{`
        .input-cx { background: rgba(255,255,255,0.6); border: 1px solid var(--border-subtle); border-radius: 12px; }
        .input-cx:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(212,140,112,0.25); border-color: var(--accent); }
      `}</style>
    </section>
  );
}

function Field({ label, required, span = 1, children }) {
  return (
    <div className={span === 2 ? "col-span-2" : "col-span-2 md:col-span-1"}>
      <Label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)] mb-2 block">
        {label} {required && <span className="text-[color:var(--accent)]">*</span>}
      </Label>
      {children}
    </div>
  );
}
